<template>
  <section 
    id="testimonials" 
    ref="targetElement"
    class="relative py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 transition-colors duration-300 overflow-hidden"
  >
    <!-- Subtile Background Decorations -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-0 left-0 w-96 h-96 bg-blue-500/3 dark:bg-blue-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div class="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/3 dark:bg-blue-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
    </div>
    
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Überschrift - vereinheitlicht mit anderen Sections -->
      <div 
        class="text-center mb-8 sm:mb-12 lg:mb-16 transition-all duration-700 ease-out"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <h2 class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Was <span class="text-blue-600 dark:text-blue-400">Kunden</span> sagen
        </h2>
        <div class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"></div>
        <p class="mt-6 text-xl sm:text-2xl font-medium text-gray-900 dark:text-gray-100 max-w-4xl mx-auto mb-4 leading-relaxed">
          Das Ergebnis? Projekte, die pünktlich fertig werden und reibungslos laufen.
        </p>
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Echte Erfahrungen aus erfolgreichen Projekten
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>

       <!-- Empty State -->
       <div v-else-if="testimonials.length === 0" class="text-center py-16 px-4">
         <div class="max-w-md mx-auto">
           <div class="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
             <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
             </svg>
           </div>
           <p class="text-gray-500 dark:text-gray-400 text-lg">Noch keine Testimonials vorhanden.</p>
         </div>
       </div>

      <!-- Desktop & Mobile Views -->
      <div v-else>
         <!-- Desktop: Grid (immer 3 Spalten) -->
         <div class="hidden lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
           <div
             v-for="testimonial in testimonials"
             :key="testimonial.id"
             class="testimonial-card bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:scale-[1.02] hover:border-blue-300 dark:hover:border-blue-700 relative overflow-hidden group"
           >
             <!-- Decorative Background Element -->
             <div class="absolute top-0 right-0 w-32 h-32 bg-blue-500/3 dark:bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500"></div>
             
             <div class="relative z-10 flex flex-col h-full">
               <!-- Profile Section - OBEN -->
               <div class="flex flex-col items-center mb-6">
                 <!-- Profilbild - GRÖSSER -->
                 <div class="mb-4 relative group/image">
                   <div class="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-50 scale-110 group-hover/image:opacity-60 group-hover/image:scale-120 transition-all duration-300"></div>
                   <img
                     :src="testimonial.imageUrl"
                     :alt="testimonial.name"
                     class="relative w-32 h-32 rounded-full object-cover ring-4 ring-white dark:ring-gray-800 shadow-lg group-hover/image:scale-105 group-hover/image:ring-gray-300 dark:group-hover/image:ring-gray-600 group-hover/image:shadow-xl group-hover/image:shadow-blue-500/20 transition-all duration-300 cursor-pointer"
                     loading="lazy"
                     width="128"
                     height="128"
                   />
                 </div>
                 
                 <!-- Name, Rolle, Firma -->
                 <div class="text-center">
                   <p class="font-bold text-gray-900 dark:text-white text-lg mb-1">
                     {{ testimonial.name }}
                   </p>
                   <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
                     {{ testimonial.role }}
                   </p>
                   
                   <!-- Firma - klickbar wenn Projekt verknüpft -->
                   <button
                     v-if="testimonial.projectId && getProjectForTestimonial(testimonial)"
                     @click="navigateToProject(testimonial)"
                     class="inline-flex items-center space-x-1 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all group/link"
                   >
                     <span>{{ testimonial.company }}</span>
                     <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                     </svg>
                   </button>
                   <p v-else class="text-sm font-semibold text-blue-600 dark:text-blue-400">
                     {{ testimonial.company }}
                   </p>
                 </div>
               </div>

               <!-- Divider -->
               <div class="w-16 h-1 bg-blue-400 rounded-full mx-auto mb-6"></div>

               <!-- Quote Icon -->
               <div class="flex justify-center mb-4">
                 <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                   <svg class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                     <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                   </svg>
                 </div>
               </div>

               <!-- Text -->
               <div class="flex-1 mb-6">
                 <p class="text-gray-700 dark:text-gray-300 text-center leading-relaxed text-[15px]">
                   {{ testimonial.text }}
                 </p>
               </div>

               <!-- Rating (optional) - UNTEN -->
               <div v-if="testimonial.rating" class="flex justify-center mt-auto pt-4 border-t border-gray-200 dark:border-gray-700">
                 <div class="flex space-x-1">
                   <svg
                     v-for="star in 5"
                     :key="star"
                     class="w-5 h-5"
                     :class="star <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'"
                     fill="currentColor"
                     viewBox="0 0 20 20"
                   >
                     <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                   </svg>
                 </div>
               </div>
             </div>
           </div>
         </div>

         <!-- Mobile: Slider (1 Testimonial) -->
         <div class="lg:hidden relative">
         <div class="overflow-hidden px-4 sm:px-6">
           <div
             class="flex transition-transform duration-500 ease-in-out"
             :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
           >
             <div
               v-for="testimonial in testimonials"
               :key="testimonial.id"
               class="w-full flex-shrink-0"
             >
               <div class="testimonial-card bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200 dark:border-gray-700 relative overflow-hidden max-w-md mx-auto h-full">
                 <!-- Decorative Background Element -->
                 <div class="absolute top-0 right-0 w-24 h-24 bg-blue-500/3 dark:bg-blue-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                 
                 <div class="relative z-10 flex flex-col h-full">
                   <!-- Profile Section - OBEN -->
                   <div class="flex flex-col items-center mb-6">
                     <!-- Profilbild - GRÖSSER -->
                     <div class="mb-4 relative group/image">
                       <div class="absolute inset-0 bg-blue-400 rounded-full blur-lg opacity-50 scale-110 group-hover/image:opacity-60 group-hover/image:scale-120 transition-all duration-300"></div>
                       <img
                         :src="testimonial.imageUrl"
                         :alt="testimonial.name"
                         class="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover ring-4 ring-white dark:ring-gray-800 shadow-lg group-hover/image:scale-105 group-hover/image:ring-gray-300 dark:group-hover/image:ring-gray-600 group-hover/image:shadow-xl group-hover/image:shadow-blue-500/20 transition-all duration-300 cursor-pointer"
                         loading="lazy"
                         width="128"
                         height="128"
                       />
                     </div>
                     
                     <!-- Name, Rolle, Firma -->
                     <div class="text-center">
                       <p class="font-bold text-gray-900 dark:text-white text-base sm:text-lg mb-1">
                         {{ testimonial.name }}
                       </p>
                       <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                         {{ testimonial.role }}
                       </p>
                       
                       <!-- Firma - klickbar wenn Projekt verknüpft -->
                       <button
                         v-if="testimonial.projectId && getProjectForTestimonial(testimonial)"
                         @click="navigateToProject(testimonial)"
                         class="inline-flex items-center space-x-1 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all group/link active:scale-95"
                       >
                         <span>{{ testimonial.company }}</span>
                         <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                         </svg>
                       </button>
                       <p v-else class="text-sm font-semibold text-blue-600 dark:text-blue-400">
                         {{ testimonial.company }}
                       </p>
                     </div>
                   </div>

                   <!-- Divider -->
                   <div class="w-12 h-1 bg-blue-400 rounded-full mx-auto mb-5"></div>

                   <!-- Quote Icon -->
                   <div class="flex justify-center mb-4">
                     <div class="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                       <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                         <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                       </svg>
                     </div>
                   </div>

                   <!-- Text -->
                   <div class="flex-1 mb-5">
                     <p class="text-gray-700 dark:text-gray-300 text-center leading-relaxed text-sm sm:text-[15px]">
                       {{ testimonial.text }}
                     </p>
                   </div>

                   <!-- Rating (optional) - UNTEN -->
                   <div v-if="testimonial.rating" class="flex justify-center mt-auto pt-4 border-t border-gray-200 dark:border-gray-700">
                     <div class="flex space-x-1">
                       <svg
                         v-for="star in 5"
                         :key="star"
                         class="w-4 h-4"
                         :class="star <= testimonial.rating ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'"
                         fill="currentColor"
                         viewBox="0 0 20 20"
                       >
                         <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                       </svg>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>
         </div>

         <!-- Slider Controls -->
         <!-- Mobile: Immer anzeigen wenn mehr als 1 Testimonial -->
         <!-- Desktop: Nur anzeigen wenn mehr als 3 Testimonials vorhanden -->
         <div 
           v-if="shouldShowSliderControls" 
           class="flex justify-center items-center mt-8 space-x-4 px-4"
           :class="{ 'lg:hidden': testimonials.length <= 3 }"
         >
          <!-- Previous Button -->
          <button
            @click="previousSlide"
            class="p-3 sm:p-2.5 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700 hover:scale-110 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            aria-label="Vorheriges Testimonial"
          >
            <svg class="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
 
          <!-- Dots -->
          <div class="flex space-x-3 sm:space-x-2.5 items-center">
            <button
              v-for="(testimonial, index) in testimonials"
              :key="testimonial.id"
              @click="currentSlide = index"
              class="rounded-full transition-all duration-300 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
              :class="currentSlide === index 
                ? 'w-10 h-3 sm:w-8 sm:h-2.5 bg-blue-600 dark:bg-blue-400' 
                : 'w-3 h-3 sm:w-2.5 sm:h-2.5 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'"
              :aria-label="`Zu Testimonial ${index + 1} gehen`"
            />
          </div>
 
          <!-- Next Button -->
          <button
            @click="nextSlide"
            class="p-3 sm:p-2.5 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all border border-gray-200 dark:border-gray-700 hover:scale-110 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            aria-label="Nächstes Testimonial"
          >
            <svg class="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
         </div>
      </div>
    </div>

    <!-- Project Detail Modal -->
    <ProjectDetailModal
      v-if="selectedProject"
      :is-open="isProjectModalOpen"
      :project="selectedProject"
      :accent-color="selectedProject.accentColor || '#3B82F6'"
      @close="closeProjectModal"
    />
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { getTestimonials } from '@/firebase/testimonials'
import { getPortfolioProjects } from '@/firebase/portfolio'
import type { Testimonial } from '@/types/testimonial'
import type { PortfolioProject } from '@/firebase/portfolio'
import { logger } from '@/utils/logger'
import { useScrollAnimation } from '../../composables/useScrollAnimation'
import ProjectDetailModal from '../ProjectDetailModal.vue'

const router = useRouter()

// Scroll-Animation mit Intersection Observer
const { isVisible, targetElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

// State
const testimonials = ref<Testimonial[]>([])
const loading = ref(true)
const currentSlide = ref(0)
const projects = ref<PortfolioProject[]>([])
const isProjectModalOpen = ref(false)
const selectedProject = ref<PortfolioProject | null>(null)

// Computed: Sollten Slider-Controls angezeigt werden?
// Mobile: Immer wenn mehr als 1 Testimonial vorhanden
// Desktop: Nur wenn mehr als 3 Testimonials vorhanden (da 3 nebeneinander angezeigt werden)
const shouldShowSliderControls = computed(() => {
  return testimonials.value.length > 1
})

// Auto-Rotation Removed as per request

// Slider-Funktionen
const nextSlide = () => {
  if (currentSlide.value < testimonials.value.length - 1) {
    currentSlide.value++
  } else {
    currentSlide.value = 0
  }
}

const previousSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--
  } else {
    currentSlide.value = testimonials.value.length - 1
  }
}

// Testimonials laden
const loadTestimonials = async () => {
  loading.value = true
  try {
    testimonials.value = await getTestimonials(true) // Nur sichtbare
    projects.value = await getPortfolioProjects() // Projekte für Verlinkung laden
  } catch (error) {
    logger.error('Fehler beim Laden der Testimonials', error)
  } finally {
    loading.value = false
  }
}

// Projekt zu einem Testimonial finden
const getProjectForTestimonial = (testimonial: Testimonial) => {
  if (!testimonial.projectId) return null
  return projects.value.find(p => p.id === testimonial.projectId)
}

// Projekt-Modal schließen
const closeProjectModal = () => {
  isProjectModalOpen.value = false
  // Reset nach kurzer Verzögerung, damit die Animation abgeschlossen wird
  setTimeout(() => {
    selectedProject.value = null
  }, 300)
}

// Zum Projekt navigieren oder Modal öffnen
const navigateToProject = (testimonial: Testimonial) => {
  const project = getProjectForTestimonial(testimonial)
  if (!project) return
  
  // Wenn Projekt eine eigene Detailseite hat, navigiere dorthin
  if (project.projectPageUrl) {
    router.push(project.projectPageUrl)
  }
  // Sonst öffne das Projekt-Detail-Modal direkt
  else {
    selectedProject.value = project
    isProjectModalOpen.value = true
  }
}

onMounted(async () => {
  await loadTestimonials()
})

onUnmounted(() => {
  // Cleanup removed
})
</script>

<style scoped>
.testimonial-card {
  min-height: 420px;
}

@media (max-width: 768px) {
  .testimonial-card {
    min-height: 400px;
  }
}

/* Optimierte Animation für bessere Performance */
@supports (will-change: transform) {
  .testimonial-card {
    will-change: transform;
  }
}

/* Reduzierte Animationen für Nutzer mit prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .testimonial-card {
    transition: none;
  }
  
  .testimonial-card:hover {
    transform: none;
  }
}
</style>

